import jQueryAjaxRetry from '../../shared/helpers/jQueryAjaxRetry';
import { store } from 'crazyEditor/redux/store';
import { getSaveData } from 'crazyEditor/redux/modules/element/elementSelectors';
import {addNotification} from 'shared/helpers/onScreenNotificationHelper';
import _ from 'lodash';
import shallowEqual from 'fbjs/lib/shallowEqual';
import { getPublishCode } from 'crazyEditor/core/publish/publish';
import {onTemplateSave} from 'shared/components/Integration/IntegrationUtils';
import { openLazyPanel, closeLazyPanel } from 'crazyEditor/redux/modules/panel/panel';
import { TYPES, EDITOR_PANELS } from 'crazyEditor/core/constants/panelConstants';
import {updateTemplateInfo} from 'crazyEditor/redux/modules/template/template';
import { saveSite, updateMobileView, clearSaveError, saveSiteFail, saveSiteSuccess} from 'crazyEditor/redux/modules/editor/editor';
import {TEMPLATE_NAME_DIALOG_TYPE} from 'crazyEditor/components/TemplateNameDialog/TemplateNameDialog';
import updateQuery from 'shared/helpers/updateQuery';
export function getOriginTemplateId() {
  return store.getState().template.id;
}

class TemplateService {
  constructor() {
    this.lastAutosaveSaveData = null;
    this.preventConfirmNavigation();
  }

  immediatePreventConfirmNavigation = () => {
    this.lastSaveData = getSaveData(store.getState());
  };

  preventConfirmNavigation() {
    window.setTimeout(() => {
      this.immediatePreventConfirmNavigation();
    }, 1000); // allow some time for template to settle.
  }

  getTemplateName() {
    const { template } = store.getState();
    return template.editable ? template.name : null;
  }

  getTemplateId() {
    const { template } = store.getState();
    return template.editable ? template.id : null;
  }

  hasUnsavedChanges = () => {
    const state = store.getState();
    if (state.editor.isQuickPreviewMode) {
      return true;
    }
    if (!state.template.editable && _.isEmpty(state.listingData)) {
      return true;
    }
    return !shallowEqual(this.lastSaveData, getSaveData(state));
  };

  autosave = (userInitiated = false) => {
    const state = store.getState();
    const saveData = getSaveData(state);
    if (saveData === this.lastAutosaveSaveData && !userInitiated) {
      return;
    }
    const {publishCode, templateCSS} = getPublishCode(state.user.bulkReviseEnabled);
    this.lastAutosaveSaveData = saveData;
    this.save({
      name: this.name,
      exportCode: publishCode,
      templateCSS,
      autosave: true,
      ...saveData,
    }).then(() => {
      this.lastSaveSucceeded = true;
      if (userInitiated) {
        addNotification({
          message: `<b>Autosave</b> Saved`,
          level: 'success',
          autoDismiss: 3,
        });
      }
    }).catch(() => {
      this.lastSaveSucceeded = false;
      addNotification({
        message: `Autosave failed`,
        level: 'error',
        autoDismiss: 0,
      });
    });
  };

  save(template) {
    store.dispatch(updateMobileView());
    let url = '/application/template';
    const templateId = this.getTemplateId();
    if (templateId && !template.autosave) {
      template = {...template, id: templateId};
      url += '/update';
    } else {
      url += '/create';
      template = {...template, originTemplateId: getOriginTemplateId()};
    }

    const fd = new FormData();
    fd.append('template_json', new Blob([JSON.stringify(template)], {type: 'application/json'}));

    // custom - custom data passed for integration API usage
    const custom = window.sessionStorage.getItem('integrationCustomData');
    if (custom) {
      url += `?custom=${encodeURIComponent(custom)}`;
    }
    return jQueryAjaxRetry({
      url,
      method: 'POST',
      processData: false,
      contentType: false,
      data: fd,
    });
  }

  saveCurrentTemplate = (andApplyAll = false, integrationConnector = null) => {
    let promise;
    if (_.isEmpty(this.getTemplateName())) {
      promise = new Promise((resolve, reject) => {
        store.dispatch(openLazyPanel(EDITOR_PANELS.TEMPLATE_NAME, TYPES.EDITOR_PANEL, null, {type: TEMPLATE_NAME_DIALOG_TYPE.SAVE, onClose: () => {
          if (this.lastSaveSucceeded) {
            resolve();
          } else {
            reject();
          }
        }}));
      });
    } else {
      promise = this.saveWithName(this.getTemplateName(), andApplyAll, integrationConnector);
    }
    return promise;
  };

  saveWithName = (name, andApplyAll = false, integrationConnector = null) => {
    return new Promise((resolve, reject) => {
      const state = store.getState();
      const {publishCode, templateCSS} = getPublishCode(state.user.bulkReviseEnabled);
      store.dispatch(saveSite());
      this.save({
        name: name,
        exportCode: publishCode,
        templateCSS,
        autosave: false,
        andApplyAll,
        ...getSaveData(state),
      }).then((data) => {
        if (data.success) {
          _.defer(() => {
            window.analytics.track('Template Saved', {Name: name});
          });
          this.lastSaveSucceeded = true;
          store.dispatch(saveSiteSuccess(name));
          this.preventConfirmNavigation();
          addNotification({
            message: `<b>${name}</b> has been successfully saved`,
            level: 'success',
            autoDismiss: 3,
          });
          this.updateTemplateData(name, data);
          store.dispatch(closeLazyPanel(EDITOR_PANELS.TEMPLATE_NAME));
          resolve(data);
          try {
            if (integrationConnector) {
              let integrationHtml = publishCode;
              if (data.cssUrl) {
                integrationHtml = publishCode.replace(/CL_CSS_URL_PLACEHOLDER/g, data.cssUrl);
              }
              onTemplateSave(integrationConnector, {id: this.getTemplateId(), html: integrationHtml, name: this.getTemplateName()
              });
            }
          } catch (e) {
            // ignore integration errors
          }
        }else {
          this.lastSaveSucceeded = false;
          store.dispatch(saveSiteFail(data.error));
          reject();
        }
      }).catch((jqXHR, textStatus, errorThrown) => {
        this.lastSaveSucceeded = false;
        if (this.getTemplateId()) {
          addNotification({
            message: `<b>${name}</b> failed to save`,
            level: 'error',
            autoDismiss: 0,
          });
        }

        store.dispatch(saveSiteFail(jqXHR.statusText));
        reject();
      });
    }).catch( () => {});
  };

  updateTemplateData(name, data) {
    if (!_.isEmpty(store.getState().listingData)) {
      return;
    }
    const templateInfoUpdate = {};
    if (name !== this.getTemplateName()) {
      templateInfoUpdate.name = name;
    }
    if (data.id) {
      templateInfoUpdate.id = data.id;
      templateInfoUpdate.editable = true;
      updateQuery({id: data.id, itemId: null}, true);
    }
    if (!_.isEmpty(templateInfoUpdate)) {
      store.dispatch(updateTemplateInfo(templateInfoUpdate));
    }
  }
}

export let templateService = null;
let templateInterval = null;
export function closeTemplateService() {
  window.clearInterval(templateInterval);
  templateInterval = null;
}

export function createTemplateService() {
  if (templateInterval !== null) {
    closeTemplateService();
  }
  templateService = new TemplateService();
  templateInterval = window.setInterval(templateService.autosave, 2 * 60 * 1000);
}

